/**
 * WPML language switcher form behavior.
 *
 * Binds each rendered Bricks language-switcher form, reads per-instance JSON config
 * from PHP output, writes the grow_lang cookie, and navigates to the selected URL.
 * If the selected language is already active, we only update the cookie and close
 * the active responsive modal (if any) without reloading the page.
 *
 * Uses `window.growtherapyWpmlMergeLocationSearchIntoUrl` (see
 * `js/wpml/merge-location-search-into-url.js`) so LP params survive path-keyed cache.
 */
(function () {
	/**
	 * Parse JSON config embedded by PHP for a single form instance.
	 *
	 * @param {string} configId - DOM id of the config script node.
	 * @return {Object|null} Parsed config object or null when missing/invalid.
	 */
	function parseConfig(configId) {
		if (!configId) {
			return null;
		}

		const configNode = document.getElementById(configId);
		if (!configNode || !configNode.textContent) {
			return null;
		}

		try {
			return JSON.parse(configNode.textContent);
		} catch {
			return null;
		}
	}

	/**
	 * @param {string} targetUrl - Navigation target from server payload.
	 * @return {string} URL with current address bar query merged in.
	 */
	function mergeNavUrl(targetUrl) {
		if (
			typeof window.growtherapyWpmlMergeLocationSearchIntoUrl === 'function'
		) {
			return window.growtherapyWpmlMergeLocationSearchIntoUrl(targetUrl);
		}
		return targetUrl;
	}

	/**
	 * Bind submit behavior for one switcher root.
	 *
	 * @param {HTMLElement} root - Switcher root element.
	 * @return {void}
	 */
	function initSwitcher(root) {
		if (!root || root.dataset.wpmlSwitcherBound === '1') {
			return;
		}

		const formId = root.dataset.wpmlSwitcherFormId || '';
		const configId = root.dataset.wpmlSwitcherConfigId || '';
		const radioSelector =
			root.dataset.wpmlSwitcherRadioSelector || "input[type='radio']:checked";
		const form = formId
			? document.getElementById(formId)
			: root.querySelector('form');
		const payload = parseConfig(configId);

		if (!form || !payload || !payload.urlByLang) {
			return;
		}

		form.addEventListener('submit', function (event) {
			event.preventDefault();

			const selected = form.querySelector(radioSelector);
			if (!selected || !selected.value) {
				return;
			}

			const lang = selected.value;
			if (window.SegmentTracking?.trackButtonClick) {
				const componentName =
					form
						.closest('[data-segment-component-name]')
						?.getAttribute('data-segment-component-name') ||
					'Language Switcher Modal';
				window.SegmentTracking.trackButtonClick(
					`Language Switcher Modal Save [${lang}] - Click`,
					{
						component_name: componentName,
						selected_language: lang,
						current_language: payload.currentLang || '',
					},
				);
			}
			const url =
				payload.urlByLang[lang] || payload.urlByLang[payload.currentLang] || '';
			if (!url) {
				return;
			}

			if (typeof window.growtherapyWpmlWriteLanguageCookie === 'function') {
				window.growtherapyWpmlWriteLanguageCookie(payload, lang);
			}

			// If user selected current language, keep page in place and close modal.
			if (payload.currentLang && lang === payload.currentLang) {
				const modalManager = window.growtherapyResponsiveModalManager;
				if (
					modalManager &&
					typeof modalManager.closeActiveModal === 'function'
				) {
					modalManager.closeActiveModal();
				}
				return;
			}

			window.location.href = mergeNavUrl(url);
		});

		root.dataset.wpmlSwitcherBound = '1';
	}

	/**
	 * Initialize all switcher instances on the current page.
	 *
	 * @return {void}
	 */
	function initAll() {
		const roots = document.querySelectorAll('[data-wpml-switcher-form-id]');
		for (let i = 0; i < roots.length; i++) {
			initSwitcher(roots[i]);
		}
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', initAll);
	} else {
		initAll();
	}
})();
